import { useEffect, useRef } from 'react';
import { createContextLogger } from '../utils/logger';

const logger = createContextLogger('usePerformanceMonitor');
import { telemetryService } from '../api';

const isDevelopment: boolean = process.env.NODE_ENV === 'development';

interface PropChangeEntry {
  key: string;
  value: any;
}

// Performance thresholds for telemetry tracking
const PERFORMANCE_THRESHOLDS = {
  SLOW_RENDER_MS: 16, // > 16ms is considered slow (60fps)
  EXCESSIVE_RENDERS: 10, // More than 10 renders in a short time
  LARGE_PROP_COUNT: 20, // Large number of props
};

export const usePerformanceMonitor = (
  componentName: string, 
  props: Record<string, any> = {}
): void => {
  // Always call hooks - move condition inside useEffect
  const renderCount = useRef<number>(0);
  const lastRenderTime = useRef<number>(performance.now());
  const propChanges = useRef<Map<string, any>>(new Map());

  useEffect(() => {
    renderCount.current += 1;
    const currentTime = performance.now();
    const timeSinceLastRender = currentTime - lastRenderTime.current;
    lastRenderTime.current = currentTime;

    // Track prop changes
    const propChangeCount = Object.entries(props).reduce((count, [key, value]) => {
      const prevValue = propChanges.current.get(key);
      if (JSON.stringify(prevValue) !== JSON.stringify(value)) {
        propChanges.current.set(key, value);
        return count + 1;
      }
      return count;
    }, 0);

    // Log performance data in development
    // if (isDevelopment) {
    //   logger.performance.debug('Component performance:', {
    //     componentName,
    //     renderCount: renderCount.current,
    //     timeSinceLastRender,
    //     timestamp: Date.now(),
    //     propChangeCount,
    //   });
    // }

    // Track performance issues with telemetry (in all environments)
    if (timeSinceLastRender > PERFORMANCE_THRESHOLDS.SLOW_RENDER_MS) {
      telemetryService.trackEvent('slow-component-render', {
        componentName,
        renderTime: timeSinceLastRender,
        renderCount: renderCount.current,
        propCount: Object.keys(props).length,
        propChangeCount,
        timestamp: Date.now()
      });
    }

    if (renderCount.current > PERFORMANCE_THRESHOLDS.EXCESSIVE_RENDERS) {
      telemetryService.trackEvent('excessive-component-renders', {
        componentName,
        renderCount: renderCount.current,
        averageRenderTime: timeSinceLastRender,
        propCount: Object.keys(props).length,
        timestamp: Date.now()
      });
    }

    if (Object.keys(props).length > PERFORMANCE_THRESHOLDS.LARGE_PROP_COUNT) {
      telemetryService.trackEvent('large-prop-count', {
        componentName,
        propCount: Object.keys(props).length,
        renderCount: renderCount.current,
        timestamp: Date.now()
      });
    }

    return () => {
      // Track component lifecycle telemetry
      if (renderCount.current > 0) {
        telemetryService.trackEvent('component-unmount', {
          componentName,
          totalRenders: renderCount.current,
          componentLifetime: Date.now() - (currentTime - (timeSinceLastRender * renderCount.current)),
          timestamp: Date.now()
        });
      }

      // Development-only logging
      // if (isDevelopment) {
      //   logger.performance.debug('Component unmount:', {
      //     componentName,
      //     totalRenders: renderCount.current,
      //     timestamp: Date.now(),
      //   });
      // }
    };
  }, [componentName, props]);
};
